import React, { memo, useRef, useMemo, useEffect } from 'react';
import Animated, { withTiming, useSharedValue, useAnimatedStyle, } from 'react-native-reanimated';
import { View } from 'react-native';
import { CardView, rem } from 'design-system';
import LineChart from 'design-system/LineChart/LineChart';
import useLineGraphData from 'design-system/LineChart/useLineGraphData';
import ChartLoadingOverlay from 'design-system/LineChart/ChartLoadingOverlay';
import ScrollableTimeFrameController from 'design-system/LineChart/ScrollableTimeFrameController';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatCryptoAmount } from 'utils/formatting';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { getPointsFromData } from 'features/accounts/hooks/useOverviewChart';
import { isWeb } from '../../../../constants';
import InvestmentInfoView from './InvestmentInfoView';
const PADDING_HORIZONTAL = rem(16);
const DEFAULT_PERIOD = '3M';
const PERIODS = [
    '1W',
    '1M',
    '3M',
    '6M',
    'YTD',
    '1Y',
    'ALL',
];
const CHART_HEIGHT = rem(243);
const ACTUAL_CHART_HEIGHT = CHART_HEIGHT - rem(24 * 2);
const withSecondaryLabels = (items, isCrypto) => items.map((item) => {
    if (typeof item.convertedBalance !== 'number' || !item.convertedCurrency)
        return item;
    if (isCrypto)
        return {
            ...item,
            balance: item.convertedBalance,
            currency: item.convertedCurrency,
            secondaryLabel: formatCryptoAmount(item.balance, item.currency),
        };
    return {
        ...item,
        secondaryLabel: formatAmount(item.convertedBalance, item.convertedCurrency, 2),
    };
});
const AccountsInvestmentHeader = ({ data }) => {
    const styles = useStyles(styleSet);
    const isCrypto = data.type === 'CRYPTO';
    const opacity = useSharedValue(0);
    const [renderGraph, setRenderGraph] = React.useState(false);
    const investmentBalances = useAppSelector((store) => store.accounts.investmentBalances);
    const cache = useRef({
        '1W': null,
        '1M': null,
        '3M': null,
        '6M': null,
        YTD: null,
        '1Y': null,
        ALL: null,
    });
    const { portfolioDataSet, setPortfolioDataSet, selectedPeriod, setSelectedPeriod, requesting, setRequesting, selectedInfoObj, } = useLineGraphData(PERIODS, DEFAULT_PERIOD);
    const previousInvestmentBalances = useRef(investmentBalances);
    useEffect(() => {
        if (cache.current[selectedPeriod]?.length &&
            previousInvestmentBalances.current === investmentBalances) {
            setRequesting(false);
            setPortfolioDataSet((oldDataSet) => ({
                ...oldDataSet,
                ...cache.current,
            }));
        }
        else if (investmentBalances?.periods) {
            const data = (() => {
                switch (selectedPeriod) {
                    case '1M': {
                        if (investmentBalances?.periods.month?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.month, isCrypto)).points;
                        }
                        return null;
                    }
                    case '1Y': {
                        if (investmentBalances?.periods.year?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.year, isCrypto)).points;
                        }
                        return null;
                    }
                    case '1W': {
                        if (investmentBalances?.periods.week?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.week, isCrypto)).points;
                        }
                        return null;
                    }
                    case '3M': {
                        if (investmentBalances?.periods.threemonths?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.threemonths, isCrypto)).points;
                        }
                        return null;
                    }
                    case '6M': {
                        if (investmentBalances?.periods.sixmonths?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.sixmonths, isCrypto)).points;
                        }
                        return null;
                    }
                    case 'YTD': {
                        if (investmentBalances?.periods.ytd?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.ytd, isCrypto)).points;
                        }
                        return null;
                    }
                    case 'ALL': {
                        if (investmentBalances?.periods.ALL?.length) {
                            return getPointsFromData(withSecondaryLabels(investmentBalances.periods.ALL, isCrypto)).points;
                        }
                        return null;
                    }
                    default: {
                        return null;
                    }
                }
            })();
            if (data) {
                cache.current[selectedPeriod] = data;
                setRequesting(false);
                setPortfolioDataSet((oldData) => ({
                    oldData,
                    [selectedPeriod]: data,
                }));
            }
            else {
                setRequesting(true);
            }
        }
        previousInvestmentBalances.current = investmentBalances;
    }, [
        investmentBalances,
        isCrypto,
        selectedPeriod,
        setPortfolioDataSet,
        setRequesting,
    ]);
    React.useEffect(() => {
        let timeout;
        // eslint-disable-next-line no-undef
        requestAnimationFrame(() => {
            timeout = setTimeout(() => {
                setRenderGraph(true);
                opacity.value = withTiming(1);
            }, 300);
        });
        return () => {
            clearTimeout(timeout);
        };
    }, [opacity]);
    const animatedStyles = useAnimatedStyle(() => ({
        opacity: opacity.value,
    }), [opacity]);
    const points = useMemo(() => {
        if (portfolioDataSet[selectedPeriod]) {
            return portfolioDataSet[selectedPeriod];
        }
        return [];
    }, [portfolioDataSet, selectedPeriod]);
    const { width } = useAppFrameDimensions(isWeb);
    const graph = useMemo(() => {
        if (!renderGraph)
            return null;
        return (<Animated.View style={animatedStyles} pointerEvents="box-none">
        <LineChart points={points} width={width - rem(32)} height={ACTUAL_CHART_HEIGHT} selectedInfoObj={selectedInfoObj} chartWrapperHeight={CHART_HEIGHT}/>
      </Animated.View>);
    }, [animatedStyles, points, renderGraph, selectedInfoObj, width]);
    return (<CardView noMarginBottom>
      <View style={styles.graphContainer}>
        <InvestmentInfoView data={data} selectedInfoObj={selectedInfoObj} lastItem={points[points.length - 1]}/>
        <View style={styles.graphTop}>
          <View style={styles.chartContainer}>{graph}</View>
          <View style={styles.flex}/>
          <ChartLoadingOverlay requesting={requesting}/>
        </View>
        <ScrollableTimeFrameController periods={PERIODS} darkActiveIndicator selectedPeriod={selectedPeriod} paddingHorizontal={PADDING_HORIZONTAL} setSelectedPeriod={setSelectedPeriod}/>
      </View>
    </CardView>);
};
const styleSet = createStyleSheets(() => ({
    graphContainer: {
        paddingBottom: rem(2),
    },
    indicator: {
        marginBottom: rem(16),
    },
    flex: {
        flex: 1,
    },
    chartContainer: {
        height: CHART_HEIGHT,
    },
    graphTop: {
        height: CHART_HEIGHT,
    },
}));
export default memo(AccountsInvestmentHeader);
